import React, { useCallback, useEffect } from "react";

import {
    LoadingScreen,
    StyleFunction,
    useAuth,
    useForm,
    useThemedStyle,
} from "@venuepos/react-common";
import { RootStackScreenProps } from "../../../navigation";
import { AdminContainer } from "../../container";
import { Form } from "../form";
import { useHandleMutationError } from "../../../hooks";
import {
    GQLayoutEntitySave,
    useLayoutEntityLazyQuery,
    useLayoutEntitySaveMutation,
} from "graphql-sdk";
import { schemaLayoutEntity } from "lib";
import { useTranslation } from "react-i18next";
import { View } from "react-native";

type ScreenProps = RootStackScreenProps<"LAYOUT_ENTITY_EDIT">;

export function LayoutEntityEditScreen({
    navigation: { navigate },
    route,
}: ScreenProps) {
    const auth = useAuth();
    auth.enforce("merchant.layout.write");

    const [t] = useTranslation();
    const [layoutEntitySave] = useLayoutEntitySaveMutation();
    const [getLayoutEntity, layoutEntity] = useLayoutEntityLazyQuery({
        fetchPolicy: "no-cache",
    });

    const form = useForm<GQLayoutEntitySave>(schemaLayoutEntity, null);
    const [{ values }, { setDefaultValues }] = form;
    const styles = useThemedStyle(styleFunc);
    const { handleMutationError } = useHandleMutationError();
    const layoutEntityId = route.params.id;

    useEffect(() => {
        if (!layoutEntityId) {
            navigate("LAYOUT_ENTITIES");
            return;
        }

        getLayoutEntity({ variables: { id: layoutEntityId } });
    }, [getLayoutEntity, navigate, layoutEntityId]);

    useEffect(() => {
        if (layoutEntity.data && layoutEntity.data.layoutEntity) {
            const layoutEntityData = layoutEntity.data.layoutEntity;

            setDefaultValues({
                name: layoutEntityData.name || "",
                data: JSON.stringify(layoutEntityData.data) || {},
                type: layoutEntityData.type || "FUNCTION",
                function: layoutEntityData.function || undefined,
            });
        }
    }, [layoutEntity.data, setDefaultValues]);

    const handleEdit = useCallback(async () => {
        if (!layoutEntity.data || !layoutEntity.data.layoutEntity || !values) {
            return;
        }

        await handleMutationError(
            async () =>
                await layoutEntitySave({
                    variables: {
                        id: layoutEntity.data!.layoutEntity.id,
                        layoutEntity: {
                            name: values.name,
                            data: values.data,
                            type: values.type,
                            function: values.function,
                        },
                    },
                }),
            t("common.saved", "Saved"),
            () => {
                navigate("LAYOUT_ENTITIES", { refetch: true });
            }
        );
    }, [
        layoutEntity.data,
        values,
        handleMutationError,
        t,
        layoutEntitySave,
        navigate,
    ]);

    if (!layoutEntity.data || !layoutEntity.data.layoutEntity) {
        return <LoadingScreen style="light" />;
    }

    return (
        <AdminContainer>
            <View>
                <View style={styles.formBox}>
                    <Form
                        form={form}
                        onSubmit={handleEdit}
                        submitButton={["common.save", "Save"]}
                        isEditing={true}
                    />
                </View>
            </View>
        </AdminContainer>
    );
}

const styleFunc: StyleFunction = theme => ({
    formBox: {
        maxWidth: theme.dimensions.maxFormWidth,
    },
});
